import { TFunction } from 'i18next';
import { CheckoutConfig, Transaction } from '../../types/checkout';
import { ExportToCsv } from 'export-to-csv';
import { formatDate, formatPhoneNumber, formatTime, toCommonCurrencyAmount } from '../../helpers/formatters';
import {
    getCapturedAmount,
    getCapturedVatAmount,
    getCurrentEvent,
    getCustomerName,
    getProductName,
    getRefundedAmount
} from '../transactionHelpers';
import { getLocaleNumeralDecimal } from '../../helpers/intl';
import { downloadDataTablePdf } from '../../helpers/download/dataTablePdf';
import { SalesLocation } from '../../types/customers';

export enum ActionTypes {
    GetFilterData = '[transactions] GET_FILTER_DATA',
    FetchFilterDataResponse = '[transactions] FETCH_FILTER_DATA_RESPONSE',
    GetTransactions = '[transactions] GET_TRANSACTIONS',
    FetchTransactionsResponse = '[transactions] FETCH_TRANSACTIONS_RESPONSE',
    FetchNextResponse = '[transactions] FETCH_NEXT_RESPONSE',
    SwapTransaction = '[transactions] SWAP_TRANSACTION',
}

export enum GetTransactionReason {
    mount = 'mount',
    update = 'update',
    next = 'next',
    refresh = 'refresh',
}

export interface GetFilterData {
    type: ActionTypes.GetFilterData;
    payload: {
        accountIds: string[];
        fetchLocations: boolean;
    };
}

export function getFilterData(accountIds: string[], fetchLocations: boolean): GetFilterData {
    return {
        type: ActionTypes.GetFilterData,
        payload: {
            accountIds: accountIds,
            fetchLocations,
        },
    };
}

export interface FetchFilterDataResponse {
    type: ActionTypes.FetchFilterDataResponse;
    payload: {
        configurations: { [accountId: string]: CheckoutConfig };
        locations: { [accountId: string]: SalesLocation[] };
    };
}

export interface GetTransactions {
    type: ActionTypes.GetTransactions;
    payload: {
        accountId: string;
        params: URLSearchParams;
        reason: GetTransactionReason;
        requestId: number;
        limit?: number;
    };
}

export interface FetchTransactionsResponse {
    type: ActionTypes.FetchTransactionsResponse;
    payload: {
        transactions: Transaction[];
    };
    meta: {
        cause: GetTransactions;
    };
}

export interface FetchNextResponse {
    type: ActionTypes.FetchNextResponse;
    payload: {
        transactions: Transaction[];
    };
    meta: {
        cause: FetchTransactionsResponse;
    };
}

export function getTransactions(
    accountId: string,
    params: URLSearchParams,
    origin: GetTransactionReason,
    limit?: number
): GetTransactions {
    return {
        type: ActionTypes.GetTransactions,
        payload: {
            accountId,
            params,
            reason: origin,
            requestId: performance.now(),
            limit,
        },
    };
}

export interface SwapTransaction {
    type: ActionTypes.SwapTransaction;
    payload: {
        transaction: Transaction;
    };
}

const getCustomerProperty = (txn: Transaction, property: 'phone_number' | 'email') => {
    if (txn.billing_address && txn.billing_address[property]) {
        return txn.billing_address[property] || '';
    }
    if (txn.shipping_address && txn.shipping_address[property]) {
        return txn.shipping_address[property] || '';
    }
    return '';
};

const getPayoutDestinationIds = (txn: Transaction) => {
    if (txn.payout_destination_id) {
        return txn.payout_destination_id;
    }
    if ((txn.items || []).some(i => i.splits)) {
        return txn.items?.flatMap(i => (i.splits || []).map(s => s.payout_destination_id)).join(', ');
    }
    return '';
};

const mapTransactionLine = (txn: Transaction, t: TFunction, language: string) => ({
    [t('payments.fields.merchant_reference_non_abreviated')]: [txn.merchant_reference, txn.merchant_reference_2]
        .filter((x) => x)
        .join('/ '),
    [t('payments.fields.date')]: formatDate(txn.created_at, language),
    [t('payments.fields.time')]: formatTime(txn.created_at, language),
    [t('payments.fields.currency')]: txn.currency,
    [t('payments.fields.amount')]: toCommonCurrencyAmount(txn.amount, txn.currency),
    [t('payments.fields.captured')]: toCommonCurrencyAmount(getCapturedAmount(txn), txn.currency),
    [t('payments.fields.refunded')]: toCommonCurrencyAmount(getRefundedAmount(txn), txn.currency),
    [t('payments.fields.vat_captured')]: toCommonCurrencyAmount(getCapturedVatAmount(txn), txn.currency),
    [t('payments.fields.status')]: t(
        `payments.transaction_statuses.${txn.status || getCurrentEvent(txn).transaction_status}`
    ),
    [t('payments.fields.payment_processor')]: t(`payments.payment_product.${txn.payment_product}`),
    [t('payments.fields.store')]: txn.store?.id ?? '',
    [t('payments.fields.type')]: t(getProductName(txn)),
    [t('payments.fields.card')]: (txn.payment_product === 'payex' && txn.card && txn.card.masked_pan) || '',
    [t('payments.fields.customer')]: getCustomerName(txn),
    [t('payments.fields.phone_number')]: formatPhoneNumber(getCustomerProperty(txn, 'phone_number')),
    [t('payments.fields.email')]: getCustomerProperty(txn, 'email'),
    'Dintero ID': txn.id,
    [t('payments.fields.seller_ids')]: getPayoutDestinationIds(txn),
});

export function createTransactionsCsv(aid: string, transactions: Transaction[], t: TFunction, language: string): void {
    if (transactions.length === 0) {
        return;
    }
    const data = transactions.map((txn) => mapTransactionLine(txn, t, language));
    const now = new Date().toISOString();
    const nowFormatted = `${formatDate(now, language)}T${formatTime(now, language)}`;
    const options = {
        fieldSeparator: ';',
        quoteStrings: '"',
        decimalSeparator: getLocaleNumeralDecimal(language),
        showLabels: true,
        showTitle: true,
        title: `Dintero transactions download ${aid}, ${nowFormatted}`,
        filename: `${aid}_${nowFormatted.replace(':', '-')}_Dintero-transactions`,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
    };
    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(data);
}

export function createTransactionsPdf(aid: string, transactions: Transaction[], t: TFunction, language: string): void {
    if (transactions.length === 0) {
        return;
    }
    const data = transactions.map((txn) => mapTransactionLine(txn, t, language));
    const now = new Date().toISOString();
    const nowFormatted = `${formatDate(now, language)}T${formatTime(now, language)}`;
    downloadDataTablePdf(
        aid,
        `Dintero transactions download ${aid}, ${nowFormatted}`,
        nowFormatted,
        data,
        language,
        `${aid}_${nowFormatted.replace(':', '-')}_Dintero-transactions`
    );
}

export type Actions =
    | GetTransactions
    | FetchTransactionsResponse
    | FetchNextResponse
    | SwapTransaction
    | GetFilterData
    | FetchFilterDataResponse;
